import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useHasTicker} from "../../Components/TickerBar";
import {message} from "antd";
import {Helmet} from "react-helmet";
import Logo from "../../assets/Logo.png";
import {language} from "../../language/Language";
import {
    useSendResetOtpMutation,
    useVerifyResetOtpMutation,
    useResetPasswordWithOtpMutation,
} from "../../RTK/Slices/Slices/apiSliceMeat";

const texts = {
    ar: {
        title: "نسيت كلمة المرور",
        stepPhone: "أدخل رقم الهاتف المسجل، وهنبعتلك كود التحقق على واتساب",
        phone: "رقم الهاتف",
        phonePlaceholder: "أدخل رقم الهاتف",
        sendOtp: "إرسال كود التحقق",
        sending: "جاري الإرسال...",
        stepOtp: "أدخل كود التحقق اللي وصلك على واتساب",
        otp: "كود التحقق",
        otpPlaceholder: "أدخل الكود المكون من 6 أرقام",
        verifyOtp: "تأكيد الكود",
        verifying: "جاري التأكيد...",
        resendOtp: "إعادة إرسال الكود",
        stepPassword: "أدخل كلمة المرور الجديدة",
        newPassword: "كلمة المرور الجديدة",
        newPasswordPlaceholder: "أدخل كلمة المرور الجديدة",
        confirmPassword: "تأكيد كلمة المرور",
        confirmPasswordPlaceholder: "أعد إدخال كلمة المرور",
        resetPassword: "تغيير كلمة المرور",
        resetting: "جاري التغيير...",
        passwordMismatch: "كلمتا المرور غير متطابقتين",
        backToLogin: "الرجوع لتسجيل الدخول",
        genericError: "حدث خطأ، من فضلك حاول مرة أخرى",
        successReset: "تم تغيير كلمة المرور بنجاح، تقدر تسجل الدخول دلوقتي",
    },
    en: {
        title: "Forgot Password",
        stepPhone: "Enter your registered phone number, we'll send a verification code on WhatsApp",
        phone: "Phone Number",
        phonePlaceholder: "Enter phone number",
        sendOtp: "Send Verification Code",
        sending: "Sending...",
        stepOtp: "Enter the verification code sent to your WhatsApp",
        otp: "Verification Code",
        otpPlaceholder: "Enter the 6-digit code",
        verifyOtp: "Verify Code",
        verifying: "Verifying...",
        resendOtp: "Resend Code",
        stepPassword: "Enter your new password",
        newPassword: "New Password",
        newPasswordPlaceholder: "Enter new password",
        confirmPassword: "Confirm Password",
        confirmPasswordPlaceholder: "Re-enter password",
        resetPassword: "Change Password",
        resetting: "Changing...",
        passwordMismatch: "Passwords do not match",
        backToLogin: "Back to Login",
        genericError: "Something went wrong, please try again",
        successReset: "Password changed successfully, you can log in now",
    },
};

const t = texts[language === "en" ? "en" : "ar"];

const inputStyle = {
    width: "100%",
    padding: "14px 16px",
    borderRadius: "10px",
    border: "1px solid #ddd",
    fontSize: "15px",
    fontFamily: "Cairo, sans-serif",
    boxSizing: "border-box",
};

const buttonStyle = {
    width: "100%",
    background: "linear-gradient(135deg, #c0392b 0%, #922b21 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "14px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    fontFamily: "Cairo, sans-serif",
};

const ForgotPassword = () => {
    const hasTicker = useHasTicker();
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();

    const [step, setStep] = useState(1); // 1: phone, 2: otp, 3: new password
    const [mobile, setMobile] = useState("");
    const [otp, setOtp] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [sendResetOtp, {isLoading: isSending}] = useSendResetOtpMutation();
    const [verifyResetOtp, {isLoading: isVerifying}] = useVerifyResetOtpMutation();
    const [resetPasswordWithOtp, {isLoading: isResetting}] = useResetPasswordWithOtpMutation();

    const handleSendOtp = async (e) => {
        e.preventDefault();
        try {
            const response = await sendResetOtp({mobile}).unwrap();
            if (response?.success !== 1) {
                messageApi.error(response?.msg || t.genericError);
                return;
            }
            messageApi.success(response.msg);
            setStep(2);
        } catch (err) {
            messageApi.error(err?.data?.msg || t.genericError);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        try {
            const response = await verifyResetOtp({mobile, otp}).unwrap();
            if (response?.success !== 1) {
                messageApi.error(response?.msg || t.genericError);
                return;
            }
            setResetToken(response.reset_token);
            messageApi.success(response.msg);
            setStep(3);
        } catch (err) {
            messageApi.error(err?.data?.msg || t.genericError);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            messageApi.error(t.passwordMismatch);
            return;
        }
        try {
            const response = await resetPasswordWithOtp({
                mobile,
                reset_token: resetToken,
                password: newPassword,
            }).unwrap();
            if (response?.success !== 1) {
                messageApi.error(response?.msg || t.genericError);
                return;
            }
            messageApi.success(t.successReset);
            navigate("/Login", {replace: true});
        } catch (err) {
            messageApi.error(err?.data?.msg || t.genericError);
        }
    };

    return (
        <div
            dir="rtl"
            style={{
                minHeight: "100vh",
                background: "linear-gradient(135deg, #f5f0eb 0%, #ede8e3 100%)",
                fontFamily: "Cairo, sans-serif",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                paddingTop: hasTicker ? 152 : 110,
                paddingBottom: "80px",
                transition: "padding-top 0.3s",
            }}
        >
            <Helmet>
                <title>Meat-House El-Fardos - {t.title}</title>
            </Helmet>
            {contextHolder}

            <div style={{marginBottom: "24px", textAlign: "center"}}>
                <img src={Logo} alt="Meat House Al Fardous" style={{width: "160px", height: "auto"}} />
            </div>

            <div style={{width: "100%", maxWidth: "420px", padding: "0 16px"}}>
                <div
                    style={{
                        background: "#fff",
                        borderRadius: "16px",
                        padding: "28px 24px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    }}
                >
                    <h1 style={{fontSize: "22px", fontWeight: "bold", marginBottom: "8px", textAlign: "center"}}>
                        {t.title}
                    </h1>

                    {step === 1 && (
                        <form onSubmit={handleSendOtp}>
                            <p style={{color: "#666", fontSize: "14px", textAlign: "center", marginBottom: "20px"}}>
                                {t.stepPhone}
                            </p>
                            <label style={{display: "block", marginBottom: "6px", fontWeight: "bold", fontSize: "14px"}}>
                                {t.phone}
                            </label>
                            <input
                                type="tel"
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                                placeholder={t.phonePlaceholder}
                                style={{...inputStyle, marginBottom: "20px"}}
                                required
                            />
                            <button type="submit" disabled={isSending} style={buttonStyle}>
                                {isSending ? t.sending : t.sendOtp}
                            </button>
                        </form>
                    )}

                    {step === 2 && (
                        <form onSubmit={handleVerifyOtp}>
                            <p style={{color: "#666", fontSize: "14px", textAlign: "center", marginBottom: "20px"}}>
                                {t.stepOtp}
                            </p>
                            <label style={{display: "block", marginBottom: "6px", fontWeight: "bold", fontSize: "14px"}}>
                                {t.otp}
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                placeholder={t.otpPlaceholder}
                                style={{...inputStyle, marginBottom: "12px", textAlign: "center", letterSpacing: "6px"}}
                                required
                            />
                            <button
                                type="button"
                                onClick={handleSendOtp}
                                disabled={isSending}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#c0392b",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                    padding: 0,
                                    marginBottom: "20px",
                                    display: "block",
                                }}
                            >
                                {t.resendOtp}
                            </button>
                            <button type="submit" disabled={isVerifying} style={buttonStyle}>
                                {isVerifying ? t.verifying : t.verifyOtp}
                            </button>
                        </form>
                    )}

                    {step === 3 && (
                        <form onSubmit={handleResetPassword}>
                            <p style={{color: "#666", fontSize: "14px", textAlign: "center", marginBottom: "20px"}}>
                                {t.stepPassword}
                            </p>
                            <label style={{display: "block", marginBottom: "6px", fontWeight: "bold", fontSize: "14px"}}>
                                {t.newPassword}
                            </label>
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder={t.newPasswordPlaceholder}
                                style={{...inputStyle, marginBottom: "16px"}}
                                required
                            />
                            <label style={{display: "block", marginBottom: "6px", fontWeight: "bold", fontSize: "14px"}}>
                                {t.confirmPassword}
                            </label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder={t.confirmPasswordPlaceholder}
                                style={{...inputStyle, marginBottom: "20px"}}
                                required
                            />
                            <button type="submit" disabled={isResetting} style={buttonStyle}>
                                {isResetting ? t.resetting : t.resetPassword}
                            </button>
                        </form>
                    )}

                    <button
                        type="button"
                        onClick={() => navigate("/Login")}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#666",
                            fontSize: "13px",
                            cursor: "pointer",
                            padding: 0,
                            marginTop: "18px",
                            display: "block",
                            textAlign: "center",
                            width: "100%",
                        }}
                    >
                        {t.backToLogin}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
